import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormLabel,
  Grid,
  Snackbar,
} from '@mui/material'
import { FeeType, vATTreatmentTypeName } from 'api/models/Common/JyrneyEnums'
import { supplierOperationsAPI } from 'api/supplier/supplierOperationsAPI'
import { SupplierOperation } from 'api/supplier/supplierOperationsAPI.types'
import { Address, Supplier } from 'api/supplier/suppliers.types'
import Checkbox from 'components/RouteWizard/Checkbox'
import Form from 'components/RouteWizard/Form'
import Input from 'components/RouteWizard/Input'
import { Select } from 'components/RouteWizard/Select'
import useFormState from 'hooks/useFormState'
import useSupplierOperations from 'hooks/useSupplierOperations'
import { useSupplierState } from 'Pages/Supplier/SupplierContext'
import { useEffect, useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { useSelector } from 'react-redux'
import ApplyToAll from '../OperatingHours/ApplyToAll'
import HoursSlider from '../OperatingHours/HoursSlider'
import { withSupplierOperation } from '../Operations.hoc'
import ServicedAreas from '../ServicedAreas'
import defaultValues from './defaultValues'
import schema from './schema'

type TaxiOperationsProps = {
  supplierOperation: SupplierOperation
  isLoadingOperations: boolean
}

const TaxiOperations = ({
  supplierOperation,
  isLoadingOperations,
}: TaxiOperationsProps) => {
  const isEmptyOpeningHours = (openingHours: any) =>
    openingHours != null &&
    typeof openingHours === 'object' &&
    Object.keys(openingHours).length === 0

  const normalizedDefaultValues = {
    ...defaultValues,
    ...supplierOperation,
    ...(isEmptyOpeningHours(supplierOperation?.openingHours) && {
      openingHours: defaultValues.openingHours,
    }),
  }

  console.log(
    isEmptyOpeningHours(supplierOperation?.openingHours),
    supplierOperation?.openingHours,
    normalizedDefaultValues,
  )

  const [formMode, setFormMode] = useState<'create' | 'edit' | 'view'>('create')
  const [formMethods, setFormMethods] = useState<UseFormReturn | null>(null)
  const supplier: Supplier = useSelector(
    (state: any) => state.suppliers.supplier,
  )
  let supplierAddress: Address | undefined = supplier.address

  const isSameAddress = useFormState('isSameAddress', formMethods)
  const adminFeePolicyChargeTypeName = useFormState(
    'adminFeePolicy.chargeTypeName',
    formMethods,
  )
  const serviceChargeTypeName = useFormState(
    'serviceChargeTypeName',
    formMethods,
  )
  const { servicedAreasToAdd } = useSupplierState()

  const {
    updateSupplierOperations,
    createSupplierOperations,
    isLoading,
    error: operationsError,
  } = useSupplierOperations()
  const [errorSubmitting, setErrorSubmitting] = useState(false)
  const error = operationsError || errorSubmitting

  const onSubmit = async (data: any) => {
    try {
      const formData = {
        ...data,
        OperationTypeName: 'Taxi',
        supplierId: supplier.id,
        operationsAddress: isSameAddress
          ? supplierAddress
          : data?.operationsAddress,
      }
      if (isEmptyOpeningHours(formData.openingHours)) {
        formData.openingHours = defaultValues.openingHours
      }
      delete formData.isSameAddress
      if (formData.adminFeePolicy?.chargeTypeName === 'None') {
        delete formData.adminFeePolicy
      }

      if (formData?.serviceChargeTypeName === 'None') {
        delete formData.serviceChargeTypeName
        delete formData.serviceChargeValue
      }

      let resp
      if (formMode === 'create') {
        resp = await createSupplierOperations(formData)
      } else {
        resp = await updateSupplierOperations(formData)
      }

      if (resp.status >= 200 && resp.status < 300) {
        if (servicedAreasToAdd.length > 0) {
          const id = resp?.data?.id ? resp?.data?.id : supplierOperation?.id
          await supplierOperationsAPI.createServicedArea(
            id,
            servicedAreasToAdd,
            formData.openingHours,
          )
        }
        setErrorSubmitting(false)
        setFormMode('view')
      } else {
        setErrorSubmitting(true)
      }
    } catch (err) {
      console.log(err)
    }
  }

  useEffect(() => {
    if (isLoadingOperations) {
      return
    }

    if (supplierOperation) {
      setFormMode('view')
    }
  }, [isLoadingOperations])

  if (isLoadingOperations) {
    return (
      <Box
        sx={{
          display: 'flex',
          gap: '10px',
          width: '100%',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        Loading Operations
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Grid container justifyContent="flex-start" sx={{ width: '70%' }}>
      <Form
        ref={(refFormMethods) => setFormMethods(refFormMethods)}
        schema={schema}
        onSubmit={(e) => {
          e
          onSubmit(e)
        }}
        defaultValues={{
          ...normalizedDefaultValues,
        }}
        showControls={false}
      >
        <FormLabel>Operating Hours</FormLabel>
        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
          <Box sx={{ flexGrow: 0.9 }}>
            <HoursSlider day="monday" disabled={formMode === 'view'} />
            <HoursSlider day="tuesday" disabled={formMode === 'view'} />
            <HoursSlider day="wednesday" disabled={formMode === 'view'} />
            <HoursSlider day="thursday" disabled={formMode === 'view'} />
            <HoursSlider day="friday" disabled={formMode === 'view'} />
            <HoursSlider day="saturday" disabled={formMode === 'view'} />
            <HoursSlider day="sunday" disabled={formMode === 'view'} />
          </Box>
          <Box sx={{ flexGrow: 0.1, marginLeft: '40px' }}>
            <ApplyToAll
              formMethods={formMethods}
              disabled={formMode === 'view'}
            />
          </Box>
        </Box>
        <Divider />
        <FormLabel>Operations Address</FormLabel>

        {formMode === 'create' && (
          <Checkbox name="isSameAddress" label="Same as company address" />
        )}
        {!isSameAddress && (
          <>
            <Input
              name="operationsAddress.addressLine1"
              label="Address line 1"
              disabled={formMode === 'view'}
            />
            <Input
              name="operationsAddress.addressLine2"
              label="Address line 2"
              disabled={formMode === 'view'}
            />
            <Input
              name="operationsAddress.town"
              label="Town"
              disabled={formMode === 'view'}
            />
            <Input
              name="operationsAddress.county"
              label="County"
              disabled={formMode === 'view'}
            />
            <Input
              name="operationsAddress.postCode"
              label="Postcode"
              disabled={formMode === 'view'}
            />
            <Input
              name="operationsAddress.country"
              label="Country"
              disabled={formMode === 'view'}
            />
          </>
        )}

        <Input
          name="operationsContact"
          label="Operations telephone number"
          disabled={formMode === 'view'}
        />
        <Input
          name="operationsEmail"
          label="Operations email"
          disabled={formMode === 'view'}
        />
        <Divider />
        <FormLabel>Serviced Areas</FormLabel>
        <ServicedAreas mode={formMode} />

        <FormLabel>Cancellation Details</FormLabel>
        <Input
          name="cancellationPolicy"
          label={`Cancellation & stand-down policy`}
          disabled={formMode === 'view'}
          multiline
          minRows={2}
          maxRows={5}
        />

        <FormLabel>Admin Fee</FormLabel>
        <Box
          component="div"
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: '20px',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
          }}
        >
          <Select
            name="adminFeePolicy.chargeTypeName"
            options={{ None: 'None', ...FeeType }}
            disabled={formMode === 'view'}
            sx={{ width: '40%' }}
          />
          {adminFeePolicyChargeTypeName?.toLowerCase() === 'fixed' && (
            <Input
              name="adminFeePolicy.price"
              label="£ value"
              disabled={formMode === 'view'}
              inputProps={{
                step: 1,
                min: 0.0,
                max: 9999,
                type: 'number',
              }}
            />
          )}
          {adminFeePolicyChargeTypeName?.toLowerCase() === 'percentage' && (
            <Input
              name="adminFeePolicy.percentage"
              label="% value"
              disabled={formMode === 'view'}
              inputProps={{
                step: 1,
                min: 0.0,
                max: 9999,
                type: 'number',
              }}
            />
          )}
        </Box>

        <FormLabel>Service Charge</FormLabel>
        <Box
          component="div"
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: '20px',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
          }}
        >
          <Select
            name="serviceChargeTypeName"
            options={{ ...FeeType }}
            disabled={formMode === 'view'}
            sx={{ width: '40%' }}
          />
          {serviceChargeTypeName?.toLowerCase() !== 'none' && (
            <Input
              name="serviceChargeValue"
              label={`${
                serviceChargeTypeName?.toLowerCase() === 'fixed' ? '£' : '%'
              } Value`}
              disabled={formMode === 'view'}
              inputProps={{
                step: 1,
                min: 0.0,
                max: 9999,
                type: 'number',
              }}
            />
          )}
        </Box>
        <FormLabel>VAT Treatment</FormLabel>
        <Select
          name="vATTreatmentTypeName"
          options={vATTreatmentTypeName}
          disabled={formMode === 'view'}
        />

        <FormLabel>Driver</FormLabel>
        <Checkbox
          name="applyPrivateDriverPolicy"
          label="Apply Private Driver Policy?"
        />
        <Checkbox
          name="applyFullCoveragePolicy"
          label="Apply Full Coverage Policy?"
        />
        <Input
          name="minimumTimeRequiredForBooking"
          label="Minimum Time Required Prior to Booking (minutes)"
          disabled={formMode === 'view'}
          inputProps={{
            step: 1,
            min: 10,
            max: 99999999,
            type: 'number',
          }}
        />

        <Box
          component="div"
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            alignItems: 'flex-start',
            gap: '2px',
          }}
        >
          {formMode === 'edit' && (
            <Button color="error" onClick={() => setFormMode('view')}>
              Cancel
            </Button>
          )}
          {formMode === 'view' && (
            <Button onClick={() => setFormMode('edit')}>Edit</Button>
          )}

          {formMode !== 'view' && (
            <Button type="submit" disabled={isLoading}>
              Save
            </Button>
          )}
        </Box>
      </Form>
      <Snackbar open={error} autoHideDuration={3000}>
        <Alert severity="error">Error submitting operation details.</Alert>
      </Snackbar>
    </Grid>
  )
}
export default withSupplierOperation(TaxiOperations)
